import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AdminService } from '../services/firebaseService';
import ImageUpload from '../components/ImageUpload';
import { 
  UserIcon, 
  CameraIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const AdminProfile: React.FC = () => {
  const { admin, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // État local pour la photo (en attendant la mise à jour du contexte)
  const [currentPhoto, setCurrentPhoto] = useState<string | undefined>(admin?.photo);

  useEffect(() => {
    setCurrentPhoto(admin?.photo);
  }, [admin?.photo]);

  const handlePhotoUploaded = async (url: string) => {
    if (!admin) return;
    
    setLoading(true);
    setMessage(null);

    try {
      // Mettre à jour la photo en base de données
      await AdminService.updateAdminPhoto(admin.id, url);
      
      // Mettre à jour l'état local
      setCurrentPhoto(url);
      
      setMessage({
        type: 'success',
        text: 'Photo de profil mise à jour avec succès !'
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.message || 'Erreur lors de la mise à jour de la photo'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoRemoved = async () => {
    if (!admin) return;
    
    setLoading(true);
    setMessage(null);

    try {
      // Supprimer la photo en base de données
      await AdminService.removeAdminPhoto(admin.id);
      
      // Mettre à jour l'état local
      setCurrentPhoto(undefined);
      
      setMessage({
        type: 'success',
        text: 'Photo de profil supprimée avec succès !'
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.message || 'Erreur lors de la suppression de la photo'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin || !admin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Accès non autorisé</h3>
          <p className="mt-1 text-sm text-gray-500">
            Vous devez être connecté en tant qu'administrateur pour accéder à cette page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Mon Profil</h1>
        <p className="mt-2 text-gray-600">
          Gérez vos informations personnelles et votre photo de profil
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Photo de profil */}
        <div className="lg:col-span-1">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CameraIcon className="w-5 h-5 mr-2" />
              Photo de profil
            </h2>
            
            <div className="text-center">
              {/* Aperçu de la photo actuelle */}
              <div className="mb-6">
                {currentPhoto ? (
                  <img
                    src={currentPhoto}
                    alt="Profil"
                    className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-gray-200"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center mx-auto border-4 border-gray-200">
                    <UserIcon className="w-16 h-16 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Composant d'upload */}
              <ImageUpload
                currentImageUrl={currentPhoto}
                onImageUploaded={handlePhotoUploaded}
                onImageRemoved={handlePhotoRemoved}
                folder="admins"
                maxSize={5}
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Informations du profil */}
        <div className="lg:col-span-2">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <UserIcon className="w-5 h-5 mr-2" />
              Informations du profil
            </h2>

            <div className="space-y-6">
              {/* Message de statut */}
              {message && (
                <div className={`p-4 rounded-md ${
                  message.type === 'success' 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      {message.type === 'success' ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-400" />
                      ) : (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                      )}
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm font-medium ${
                        message.type === 'success' ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {message.text}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Informations de base */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet
                  </label>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <p className="text-gray-900">{admin.name}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <p className="text-gray-900">{admin.email}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rôle
                  </label>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      admin.role === 'super_admin' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {admin.role === 'super_admin' ? 'Super Administrateur' : 'Administrateur'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Membre depuis
                  </label>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <p className="text-gray-900">
                      {admin.createdAt.toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Permissions
                </label>
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <div className="flex flex-wrap gap-2">
                    {admin.permissions.map((permission, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                      >
                        {permission}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;
